// ======================================================================
// File: PurchaseInvoiceAttachments.jsx
// Backend Integrated Version
// ======================================================================

import React from "react";
import purchaseInvoiceService from "../../services/purchaseInvoiceService";

const PurchaseInvoiceAttachments = ({
  purchaseInvoiceId,
  attachments = [],
  setAttachments,
  isDraft,
  setDirty,
  loadInvoice
}) => {

  // ============================================================
  // Upload
  // ============================================================

  const handleFileSelect = async (event) => {

    const files = Array.from(
      event.target.files || []
    );

    if (!files.length) return;

    const allowedExtensions = ["pdf", "zip"];
    const maxSize = 512 * 1024;

    try {

      for (const file of files) {

        const extension =
          file.name
            .split(".")
            .pop()
            .toLowerCase();

        if (
          !allowedExtensions.includes(
            extension
          )
        ) {

          alert(
            `${file.name} : Only PDF and ZIP files are allowed`
          );

          return;
        }

        if (file.size > maxSize) {

          alert(
            `${file.name} exceeds 512 KB limit`
          );

          return;
        }

        await purchaseInvoiceService.uploadAttachment(
          purchaseInvoiceId,
          file
        );
      }

      alert(
        "Attachment uploaded successfully"
      );

      if (loadInvoice) {
        await loadInvoice();
      }

      setDirty?.(true);

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Failed to upload attachment"
      );

    } finally {

      event.target.value = "";

    }
  };

  // ============================================================
  // Delete
  // ============================================================

  const handleDelete = async (
    attachmentId
  ) => {

    if (!isDraft) return;

    const confirmed = window.confirm(
      "Delete attachment?"
    );

    if (!confirmed) return;

    try {

      await purchaseInvoiceService.deleteAttachment(
        attachmentId
      );

      alert(
        "Attachment deleted successfully"
      );

      if (loadInvoice) {
        await loadInvoice();
      }

      setDirty?.(true);

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Failed to delete attachment"
      );

    }
  };

  // ============================================================
  // Download
  // ============================================================

const handleDownload = async (
  attachment
) => {

  try {

    const blob =
      await purchaseInvoiceService.downloadAttachment(
        attachment.id
      );

    const url =
      window.URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      attachment.original_file_name ||
      "download";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);

  } catch (error) {

    console.error(error);

    alert(
      "Failed to download attachment"
    );
  }
};

  // ============================================================
  // Helpers
  // ============================================================

  const formatFileSize = (
    bytes
  ) => {

    if (!bytes) return "-";

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(
        bytes / 1024
      ).toFixed(2)} KB`;
    }

    return `${(
      bytes /
      1024 /
      1024
    ).toFixed(2)} MB`;
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="pi-card">

      <div className="pi-card-header">

        <div className="pi-card-title">
          Attachments ({attachments.length})
        </div>

        {isDraft && (

          <label className="pi-upload-btn">

            Upload

            <input
              type="file"
              multiple
              hidden
              accept=".pdf,.zip"
              onChange={
                handleFileSelect
              }
            />

          </label>

        )}

      </div>

      <div className="pi-attachment-table-wrapper">

        <table className="pi-attachment-table">

          <thead>

            <tr>
              <th>File Name</th>
              <th>Size</th>
              <th>Uploaded By</th>
              <th>Uploaded Date</th>
              <th>Action</th>
            </tr>

          </thead>

          <tbody>

            {attachments.length === 0 && (

              <tr>

                <td
                  colSpan="5"
                  className="pi-no-data"
                >
                  No attachments found
                </td>

              </tr>

            )}

            {attachments.map(
              (attachment) => (

                <tr
                  key={attachment.id}
                >

                  <td>
                    {
                      attachment.original_file_name
                    }
                  </td>

                  <td>
                    {formatFileSize(
                      attachment.file_size
                    )}
                  </td>

                  <td>
                    {attachment.uploaded_by ||
                      "-"}
                  </td>

                  <td>
                    {attachment.uploaded_at ||
                      "-"}
                  </td>

                  <td>

                    <div className="pi-action-buttons">

                      <button
                        className="pi-btn pi-btn-secondary"
                        onClick={() =>
                          handleDownload(
                            attachment
                          )
                        }
                      >
                        Download
                      </button>

                      {isDraft && (

                        <button
                          className="pi-btn pi-btn-danger"
                          onClick={() =>
                            handleDelete(
                              attachment.id
                            )
                          }
                        >
                          Delete
                        </button>

                      )}

                    </div>

                  </td>

                </tr>

              )
            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default PurchaseInvoiceAttachments;